import React, {
  useContext,
  useMemo,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { formatCurrency } from "../utils/formatters";
import "../css/checkout.css";

const PROMO_CODE = "KNIT10";
const PROMO_RATE = 0.1;

const INITIAL_FORM = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  postalCode: "",
  country: siteConfig.country || "ES",
  shippingMethod: "standard",
  acceptTerms: false,
};

const COUNTRIES = [
  { code: "HK", name: "Hong Kong" },
  { code: "AU", name: "Australia" },
  { code: "CA", name: "Canada" },
  { code: "JP", name: "Japan" },
  { code: "SG", name: "Singapore" },
  { code: "KR", name: "South Korea" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "ES", name: "Spain" },
  { code: "PT", name: "Portugal" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "IT", name: "Italy" },
  { code: "IE", name: "Ireland" },
  { code: "NL", name: "Netherlands" },
  { code: "BE", name: "Belgium" },
];

const numericPrice = (value) => {
  const parsed = Number(String(value ?? "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const findProduct = (id) =>
  productData.find((product) => String(product.id) === String(id));

const validateCheckout = (form) => {
  const errors = {};

  if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
    errors.email = "Enter a valid email address.";
  }
  if (!form.firstName.trim()) errors.firstName = "Enter a first name.";
  if (!form.lastName.trim()) errors.lastName = "Enter a last name.";
  if (!form.address.trim()) errors.address = "Enter a street address.";
  if (!form.city.trim()) errors.city = "Enter a city.";
  if (form.postalCode.trim().length < 3) {
    errors.postalCode = "Enter a valid postal code.";
  }
  if (!form.country) errors.country = "Select a country or region.";
  if (!form.shippingMethod) {
    errors.shippingMethod = "Select a delivery method.";
  }
  if (!form.acceptTerms) {
    errors.acceptTerms = "Accept the terms to continue.";
  }

  return errors;
};

const CheckOut = () => {
  const { cartItems = [], cartSubtotal, clearCart } = useContext(CartContext);
  const location = useLocation();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [formMessage, setFormMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cartLines = useMemo(
    () =>
      cartItems.flatMap((item) => {
        const product = findProduct(item.id);
        if (!product) return [];

        const quantity = Math.max(1, Number(item.quantity) || 1);
        const unitPrice = numericPrice(item.price) || numericPrice(product.price);
        return [{ item, product, quantity, unitPrice }];
      }),
    [cartItems]
  );

  const calculatedSubtotal = useMemo(
    () =>
      cartLines.reduce(
        (total, line) => total + line.unitPrice * line.quantity,
        0
      ),
    [cartLines]
  );
  const subtotal = Number.isFinite(Number(cartSubtotal))
    ? Number(cartSubtotal)
    : calculatedSubtotal;
  const hasPromotion = location.state?.promoCode === PROMO_CODE;
  const discount = hasPromotion ? subtotal * PROMO_RATE : 0;
  const shippingThreshold =
    Number(
      siteConfig.shipping?.freeShippingThreshold ??
        siteConfig.shippingThreshold
    ) || 0;
  const standardRate =
    shippingThreshold > 0 && subtotal >= shippingThreshold
      ? 0
      : numericPrice(siteConfig.shipping?.standardRate);
  const expressRate = numericPrice(siteConfig.shipping?.expressRate);
  const shipping =
    form.shippingMethod === "express" ? expressRate : standardRate;
  const total = Math.max(0, subtotal - discount + shipping);

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
    setFormMessage("");
  };

  const handleInputChange = (event) => {
    const { name, type, checked, value } = event.target;
    setField(name, type === "checkbox" ? checked : value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const nextErrors = validateCheckout(form);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setFormMessage(
        "Please review the highlighted fields before placing your order."
      );
      return;
    }

    setIsSubmitting(true);
    setFormMessage("");
    const orderNumber = `${Date.now()}${Math.floor(100000 + Math.random() * 900000)}`;
    const origin = window.location.origin;
    const params = new URLSearchParams({ orderNo: orderNumber, amount: total.toFixed(2), currency: siteConfig.currency, email: form.email.trim(), success_url: `${origin}/payment?state=success`, cancel_url: `${origin}/payment?state=cancel`, title: cartLines[0].product.name });
    clearCart();
    window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
  };

  const fieldError = (name) =>
    errors[name] ? `${name}-error` : undefined;

  if (cartLines.length === 0) {
    return (
      <main className="checkout-page__root" id="main-content">
        <section
          className="checkout-page__empty"
          aria-labelledby="checkout-empty-title"
        >
          <p className="checkout-page__eyebrow">Checkout</p>
          <h1 className="checkout-page__empty-title" id="checkout-empty-title">
            Your bag is empty
          </h1>
          <p className="checkout-page__empty-copy">
            Add a piece to your bag before continuing to checkout.
          </p>
          <Link className="checkout-page__empty-link" to="/shop">
            Browse the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page__root" id="main-content">
      <header className="checkout-page__masthead">
        <Link className="checkout-page__brand" to="/" aria-label="Home">
          {siteConfig.logoText || siteConfig.brandName}
        </Link>
        <p className="checkout-page__secure-note">
          Secure checkout
        </p>
      </header>

      <div className="checkout-page__layout">
        <section
          className="checkout-page__form-column"
          aria-labelledby="checkout-title"
        >
          <div className="checkout-page__heading">
            <p className="checkout-page__eyebrow">Complete your order</p>
            <h1 className="checkout-page__title" id="checkout-title">
              Checkout
            </h1>
          </div>

          <form
            className="form-checkout"
            onSubmit={handleSubmit}
            noValidate
          >
            {formMessage && (
              <div className="form-alert" role="alert">
                {formMessage}
              </div>
            )}

            <section className="form-section" aria-labelledby="contact-title">
              <div className="form-section__heading">
                <span className="form-section__number" aria-hidden="true">
                  01
                </span>
                <h2 className="form-section__title" id="contact-title">
                  Contact
                </h2>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="email">
                  Email address
                </label>
                <input
                  className="form-input"
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  inputMode="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={fieldError("email")}
                />
                {errors.email && (
                  <p className="form-error" id="email-error">
                    {errors.email}
                  </p>
                )}
              </div>
            </section>

            <section className="form-section" aria-labelledby="delivery-title">
              <div className="form-section__heading">
                <span className="form-section__number" aria-hidden="true">
                  02
                </span>
                <h2 className="form-section__title" id="delivery-title">
                  Delivery address
                </h2>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="country">
                  Country or region
                </label>
                <select
                  className="form-select"
                  id="country"
                  name="country"
                  value={form.country}
                  onChange={handleInputChange}
                  autoComplete="country"
                  aria-invalid={Boolean(errors.country)}
                  aria-describedby={fieldError("country")}
                >
                  {COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {errors.country && (
                  <p className="form-error" id="country-error">
                    {errors.country}
                  </p>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="firstName">
                    First name
                  </label>
                  <input
                    className="form-input"
                    id="firstName"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleInputChange}
                    autoComplete="given-name"
                    aria-invalid={Boolean(errors.firstName)}
                    aria-describedby={fieldError("firstName")}
                  />
                  {errors.firstName && (
                    <p className="form-error" id="firstName-error">
                      {errors.firstName}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="lastName">
                    Last name
                  </label>
                  <input
                    className="form-input"
                    id="lastName"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleInputChange}
                    autoComplete="family-name"
                    aria-invalid={Boolean(errors.lastName)}
                    aria-describedby={fieldError("lastName")}
                  />
                  {errors.lastName && (
                    <p className="form-error" id="lastName-error">
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="company">
                  Company <span className="form-label__optional">optional</span>
                </label>
                <input
                  className="form-input"
                  id="company"
                  name="company"
                  value={form.company}
                  onChange={handleInputChange}
                  autoComplete="organization"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="address">
                  Street address
                </label>
                <input
                  className="form-input"
                  id="address"
                  name="address"
                  value={form.address}
                  onChange={handleInputChange}
                  autoComplete="address-line1"
                  aria-invalid={Boolean(errors.address)}
                  aria-describedby={fieldError("address")}
                />
                {errors.address && (
                  <p className="form-error" id="address-error">
                    {errors.address}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="apartment">
                  Apartment, suite, etc. {" "}
                  <span className="form-label__optional">optional</span>
                </label>
                <input
                  className="form-input"
                  id="apartment"
                  name="apartment"
                  value={form.apartment}
                  onChange={handleInputChange}
                  autoComplete="address-line2"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="city">
                    City
                  </label>
                  <input
                    className="form-input"
                    id="city"
                    name="city"
                    value={form.city}
                    onChange={handleInputChange}
                    autoComplete="address-level2"
                    aria-invalid={Boolean(errors.city)}
                    aria-describedby={fieldError("city")}
                  />
                  {errors.city && (
                    <p className="form-error" id="city-error">
                      {errors.city}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="postalCode">
                    Postal code
                  </label>
                  <input
                    className="form-input"
                    id="postalCode"
                    name="postalCode"
                    value={form.postalCode}
                    onChange={handleInputChange}
                    autoComplete="postal-code"
                    aria-invalid={Boolean(errors.postalCode)}
                    aria-describedby={fieldError("postalCode")}
                  />
                  {errors.postalCode && (
                    <p className="form-error" id="postalCode-error">
                      {errors.postalCode}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <fieldset
              className="form-section form-fieldset"
              aria-describedby={fieldError("shippingMethod")}
            >
              <legend className="form-legend">
                <span className="form-section__number" aria-hidden="true">
                  03
                </span>
                <span className="form-section__title">Delivery method</span>
              </legend>
              <label className="form-option">
                <input
                  className="form-option__input"
                  type="radio"
                  name="shippingMethod"
                  value="standard"
                  checked={form.shippingMethod === "standard"}
                  onChange={handleInputChange}
                />
                <span className="form-option__body">
                  <span className="form-option__title">Standard delivery</span>
                  <span className="form-option__meta">3–6 working days</span>
                </span>
                <span className="form-option__price">
                  {standardRate === 0
                    ? "Complimentary"
                    : formatCurrency(standardRate)}
                </span>
              </label>
              <label className="form-option">
                <input
                  className="form-option__input"
                  type="radio"
                  name="shippingMethod"
                  value="express"
                  checked={form.shippingMethod === "express"}
                  onChange={handleInputChange}
                />
                <span className="form-option__body">
                  <span className="form-option__title">Express delivery</span>
                  <span className="form-option__meta">1–3 working days</span>
                </span>
                <span className="form-option__price">
                  {formatCurrency(expressRate)}
                </span>
              </label>
              {errors.shippingMethod && (
                <p className="form-error" id="shippingMethod-error">
                  {errors.shippingMethod}
                </p>
              )}
            </fieldset>

            <section className="form-section" aria-labelledby="payment-title">
              <div className="form-section__heading">
                <span className="form-section__number" aria-hidden="true">
                  04
                </span>
                <h2 className="form-section__title" id="payment-title">
                  Payment
                </h2>
              </div>
              <p className="form-notice" role="note">After you select Pay now, you will continue to our secure payment partner. Card details are entered only on that payment page.</p>
              <label className="form-checkbox">
                <input
                  className="form-checkbox__input"
                  name="acceptTerms"
                  type="checkbox"
                  checked={form.acceptTerms}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.acceptTerms)}
                  aria-describedby={fieldError("acceptTerms")}
                />
                <span className="form-checkbox__label">
                  I accept the {" "}
                  <Link className="form-link" to="/terms" target="_blank">
                    terms and conditions
                  </Link>
                  {" "}and acknowledge the{" "}
                  <Link className="form-link" to="/privacy" target="_blank">
                    privacy policy
                  </Link>
                  .
                </span>
              </label>
              {errors.acceptTerms && (
                <p className="form-error" id="acceptTerms-error">
                  {errors.acceptTerms}
                </p>
              )}
            </section>

            <div className="form-actions">
              <Link className="form-back-link" to="/cartpage">
                ← Return to bag
              </Link>
              <button
                className="form-submit"
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting
                  ? "Opening secure payment…"
                  : `Pay now · ${formatCurrency(total)}`}
              </button>
            </div>
          </form>
        </section>

        <aside
          className="checkout-page__summary-column"
          aria-labelledby="checkout-summary-title"
        >
          <div className="order-summary__panel">
            <h2 className="order-summary__title" id="checkout-summary-title">
              Your order
            </h2>
            <ul className="order-summary__items">
              {cartLines.map(({ item, product, quantity, unitPrice }) => (
                <li
                  className="order-summary__item"
                  key={`${item.id}-${item.size || "default"}-${
                    item.color || "default"
                  }`}
                >
                  <div className="order-summary__image-wrap">
                    <img
                      className="order-summary__image"
                      src={product.images?.[0] || product.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                    <span
                      className="order-summary__quantity"
                      aria-label={`Quantity ${quantity}`}
                    >
                      {quantity}
                    </span>
                  </div>
                  <div className="order-summary__item-copy">
                    <h3 className="order-summary__item-name">{product.name}</h3>
                    {(item.color || item.size) && (
                      <p className="order-summary__item-options">
                        {[item.color, item.size].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                  <p className="order-summary__item-price">
                    {formatCurrency(unitPrice * quantity)}
                  </p>
                </li>
              ))}
            </ul>

            <dl className="order-summary__totals">
              <div className="order-summary__row">
                <dt className="order-summary__label">Subtotal</dt>
                <dd className="order-summary__value">
                  {formatCurrency(subtotal)}
                </dd>
              </div>
              {hasPromotion && (
                <div className="order-summary__row">
                  <dt className="order-summary__label">
                    {PROMO_CODE} discount
                  </dt>
                  <dd className="order-summary__value">
                    −{formatCurrency(discount)}
                  </dd>
                </div>
              )}
              <div className="order-summary__row">
                <dt className="order-summary__label">Delivery</dt>
                <dd className="order-summary__value">
                  {shipping === 0 ? "Complimentary" : formatCurrency(shipping)}
                </dd>
              </div>
              <div className="order-summary__row order-summary__row--total">
                <dt className="order-summary__label">Total</dt>
                <dd className="order-summary__value">
                  {formatCurrency(total)}
                </dd>
              </div>
            </dl>
            <p className="order-summary__tax-note">
              Taxes included where applicable.
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
